export const MAX_SOURCE_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_IMAGE_PAYLOAD_BYTES = 5 * 1024 * 1024;

export type ImageInputErrorCode = "invalid_image_format" | "input_file_too_large" | "image_decode_failed" | "image_processing_failed" | "payload_too_large";

export class ImageInputError extends Error {
  readonly code: ImageInputErrorCode;
  constructor(code: ImageInputErrorCode, message: string) { super(message); this.code=code; }
}

const supportedTypes = ["image/jpeg", "image/png", "image/webp"];

export function dataUrlBytes(dataUrl: string): number {
  const comma = dataUrl.indexOf(",");
  if (comma < 0) return Number.POSITIVE_INFINITY;
  const payload = dataUrl.slice(comma + 1).replace(/\s/g, "");
  const padding = payload.endsWith("==") ? 2 : payload.endsWith("=") ? 1 : 0;
  return Math.max(0, Math.floor(payload.length * 3 / 4) - padding);
}

async function loadImage(file: File): Promise<{ image: HTMLImageElement; release: () => void }> {
  if (!supportedTypes.includes(file.type)) throw new ImageInputError("invalid_image_format", "请选择 JPG、PNG 或 WebP 图片");
  if (file.size > MAX_SOURCE_IMAGE_BYTES) throw new ImageInputError("input_file_too_large", "请选择不超过 10MB 的图片");
  const url = URL.createObjectURL(file);
  const release = () => URL.revokeObjectURL(url);
  try {
    const image = new Image();image.src = url;await image.decode();
    if (!image.naturalWidth || !image.naturalHeight) throw new Error("empty image");
    return { image, release };
  } catch {
    release();
    throw new ImageInputError("image_decode_failed", "图片已损坏或无法读取，请重新选择");
  }
}

function encodeImage(image: HTMLImageElement, dimension: number, quality: number): string {
  const ratio = Math.min(1, dimension / Math.max(image.naturalWidth, image.naturalHeight));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(image.naturalWidth * ratio));
  canvas.height = Math.max(1, Math.round(image.naturalHeight * ratio));
  const context = canvas.getContext("2d");
  if (!context) throw new ImageInputError("image_processing_failed", "图片暂时无法处理，请重试");
  context.fillStyle = "#fff";context.fillRect(0, 0, canvas.width, canvas.height);context.drawImage(image, 0, 0, canvas.width, canvas.height);
  try {
    const result = canvas.toDataURL("image/jpeg", quality);
    if (!result.startsWith("data:image/jpeg")) throw new Error("encode failed");
    return result;
  } catch {
    throw new ImageInputError("image_processing_failed", "图片暂时无法处理，请重试");
  }
}

export async function readImageFile(file: File): Promise<string> {
  const loaded=await loadImage(file);
  try {
    for (const attempt of [{dimension:1600,quality:.86},{dimension:1400,quality:.78},{dimension:1200,quality:.7},{dimension:960,quality:.62},{dimension:720,quality:.54}]) {
      const result=encodeImage(loaded.image,attempt.dimension,attempt.quality);
      if(dataUrlBytes(result)<=MAX_IMAGE_PAYLOAD_BYTES)return result;
    }
    throw new ImageInputError("payload_too_large", "照片处理后仍然过大，请换一张较小的图片");
  } finally {loaded.release();}
}
